/**
 * ContentHubBrowse Page
 * Main browse page for Content Hub with collapsible folder sidebar
 */

import { useState, useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Search,
  Grid,
  List,
  FileText,
  Folder,
  Plus,
  Hash,
  Image as ImageIcon,
  ChevronRight,
  ChevronDown,
  MoreHorizontal,
  FolderPlus,
  Pencil,
  Trash2,
  Loader2,
  FolderOpen,
  Database,
  PanelLeftClose,
  PanelLeftOpen,
  Filter,
  ArrowUpDown,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/components/ui/use-toast'
import { FluentCard } from '@/components/layout/page-header'
import {
  ContentItemGrid,
  ContentItemList,
  LegacyMoveItemDialog as MoveItemDialog,
  LegacyDuplicateItemDialog as DuplicateItemDialog,
  LegacyDeleteItemDialog as DeleteItemDialog,
  CreateNewDropdown,
} from '@/components/content-hub'
import {
  useContentItems,
  useContentItemsByFolder,
  useContentFolder,
  useContentFolderTree,
  useMoveContentItem,
  useDuplicateContentItem,
  useDeleteContentItem,
  useCreateFolder,
  useUpdateFolder,
  useDeleteFolder,
} from '@/lib/hooks/use-content-hub'
import type { ContentItem, ContentFolderTree } from '@/lib/api/types'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'

// ============================================
// Types
// ============================================

type ViewMode = 'grid' | 'list'
type SortField = 'name' | 'updatedAt' | 'type'
type SortOrder = 'asc' | 'desc'

interface FolderDialogState {
  open: boolean
  mode: 'create' | 'edit'
  parentId?: string
  folder?: ContentFolderTree
}

interface DeleteFolderDialogState {
  open: boolean
  folder: ContentFolderTree | null
}

// ============================================
// Constants
// ============================================

const SORT_OPTIONS = [
  { value: 'name-asc', label: 'Name (A-Z)', field: 'name', order: 'asc' },
  { value: 'name-desc', label: 'Name (Z-A)', field: 'name', order: 'desc' },
  { value: 'updatedAt-desc', label: 'Recently Updated', field: 'updatedAt', order: 'desc' },
  { value: 'updatedAt-asc', label: 'Oldest First', field: 'updatedAt', order: 'asc' },
]

const CONTENT_TYPES = [
  { value: 'all', label: 'All Types', icon: Database },
  { value: 'Text', label: 'Text Elements', icon: FileText },
  { value: 'DataPoint', label: 'Data Points', icon: Hash },
  { value: 'Image', label: 'Images', icon: ImageIcon },
] as const

type ContentTypeFilter = typeof CONTENT_TYPES[number]['value']

const LOCAL_STORAGE_VIEW_KEY = 'content-hub-view-mode'

// Helper to get initials from folder name
function getFolderInitials(name: string): string {
  const words = name.trim().split(/\s+/)
  if (words.length === 0 || !words[0]) return '??'
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase()
  }
  return (words[0][0] + (words[1]?.[0] || '')).toUpperCase()
}

// MS-style folder colors palette
const MS_FOLDER_COLORS = [
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#F59E0B', // Amber
  '#10B981', // Emerald
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
  '#14B8A6', // Teal
  '#6366F1', // Indigo
] as const

// Generate deterministic color from folder name
function getFolderColor(name: string, customColor?: string): string {
  if (customColor) return customColor
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return MS_FOLDER_COLORS[Math.abs(hash) % MS_FOLDER_COLORS.length] ?? '#6366F1'
}

// Helper to get background color style
function getColorStyle(name: string, color?: string): React.CSSProperties {
  return { backgroundColor: getFolderColor(name, color) }
}

// ============================================
// Folder Tree Item Component
// ============================================

interface FolderTreeItemProps {
  folder: ContentFolderTree
  level: number
  selectedFolderId: string | null
  expandedIds: Set<string>
  onSelect: (folderId: string | null) => void
  onToggleExpand: (folderId: string) => void
  onEdit: (folder: ContentFolderTree) => void
  onDelete: (folder: ContentFolderTree) => void
  onAddSubfolder: (parentId: string) => void
}

function FolderTreeItem({
  folder,
  level,
  selectedFolderId,
  expandedIds,
  onSelect,
  onToggleExpand,
  onEdit,
  onDelete,
  onAddSubfolder,
}: FolderTreeItemProps) {
  const isExpanded = expandedIds.has(folder.id)
  const isSelected = selectedFolderId === folder.id
  const hasChildren = folder.children && folder.children.length > 0

  return (
    <div className="group">
      <div
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all',
          'hover:bg-gray-100 dark:hover:bg-gray-800',
          isSelected && 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 font-medium'
        )}
        style={{ paddingLeft: `${12 + level * 16}px` }}
      >
        {/* Expand/Collapse Button */}
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleExpand(folder.id)
            }}
            className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
            )}
          </button>
        ) : (
          <span className="w-4" />
        )}

        {/* Folder Icon */}
        <button
          onClick={() => onSelect(folder.id)}
          className="flex items-center gap-2 flex-1 min-w-0"
        >
          <div
            className="w-6 h-6 rounded flex items-center justify-center text-white text-[10px] font-semibold shrink-0"
            style={getColorStyle(folder.name, folder.color)}
          >
            {getFolderInitials(folder.name)}
          </div>
          <span className="truncate flex-1 text-left">{folder.name}</span>
        </button>

        {/* Item Count */}
        <span className="text-xs text-gray-400 tabular-nums mr-1">{folder.itemCount || 0}</span>

        {/* Context Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-700 transition-opacity"
            >
              <MoreHorizontal className="h-3.5 w-3.5 text-gray-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => onAddSubfolder(folder.id)}>
              <FolderPlus className="h-4 w-4 mr-2" />
              Add Subfolder
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEdit(folder)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit Folder
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(folder)}
              className="text-red-600 dark:text-red-400 focus:bg-red-50 dark:focus:bg-red-900/20"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Folder
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {folder.children?.map((child) => (
            <FolderTreeItem
              key={child.id}
              folder={child}
              level={level + 1}
              selectedFolderId={selectedFolderId}
              expandedIds={expandedIds}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddSubfolder={onAddSubfolder}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ============================================
// ContentHubBrowse Component
// ============================================

export default function ContentHubBrowse() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { toast } = useToast()

  // Get selected folder from URL
  const selectedFolderId = searchParams.get('folder')

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [sidebarSearch, setSidebarSearch] = useState('')
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_VIEW_KEY)
    return (saved === 'list' ? 'list' : 'grid') as ViewMode
  })
  const [searchQuery, setSearchQuery] = useState('')
  const [sortValue, setSortValue] = useState<string>('updatedAt-desc')
  const [typeFilter, setTypeFilter] = useState<ContentTypeFilter>('all')
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set())


  // Folder dialog states
  const [folderDialog, setFolderDialog] = useState<FolderDialogState>({
    open: false,
    mode: 'create',
  })
  const [folderName, setFolderName] = useState('')
  const [folderDescription, setFolderDescription] = useState('')
  const [deleteFolderDialog, setDeleteFolderDialog] = useState<DeleteFolderDialogState>({
    open: false,
    folder: null,
  })

  // Item dialog states
  const [moveItemOpen, setMoveItemOpen] = useState(false)
  const [duplicateItemOpen, setDuplicateItemOpen] = useState(false)
  const [deleteItemOpen, setDeleteItemOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null)

  // Parse sort value
  const sortOption = SORT_OPTIONS.find((o) => o.value === sortValue) ?? SORT_OPTIONS[0]!
  const sortField = sortOption.field as SortField
  const sortOrder = sortOption.order as SortOrder

  // Build query params
  const buildQueryParams = useCallback(() => {
    const params: any = {
      orderBy: [{ field: sortField, direction: sortOrder }],
      count: true,
    }
    const filters: any[] = []
    if (searchQuery.trim()) {
      filters.push({ field: 'name', operator: 'contains', value: searchQuery.trim() })
    }
    if (typeFilter !== 'all') {
      filters.push({ field: 'type', operator: 'eq', value: typeFilter })
    }
    if (filters.length > 0) {
      params.filter = filters
    }
    return params
  }, [sortField, sortOrder, searchQuery, typeFilter])

  // Fetch data
  const queryParams = buildQueryParams()
  const { data: folderTree = [], isLoading: foldersLoading } = useContentFolderTree()
  const { data: itemsResponse, isLoading: isItemsLoading } = selectedFolderId
    ? useContentItemsByFolder(selectedFolderId, queryParams)
    : useContentItems(queryParams)
  const { data: selectedFolder } = useContentFolder(selectedFolderId || '', {
    enabled: !!selectedFolderId,
  })

  // Mutations
  const createFolderMutation = useCreateFolder({
    onSuccess: () => {
      toast({ title: 'Folder created', description: 'The folder has been created successfully.' })
      setFolderDialog({ open: false, mode: 'create' })
      setFolderName('')
      setFolderDescription('')
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const updateFolderMutation = useUpdateFolder({
    onSuccess: () => {
      toast({ title: 'Folder updated', description: 'The folder has been updated successfully.' })
      setFolderDialog({ open: false, mode: 'create' })
      setFolderName('')
      setFolderDescription('')
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const deleteFolderMutation = useDeleteFolder({
    onSuccess: () => {
      toast({ title: 'Folder deleted', description: 'The folder has been deleted successfully.' })
      setDeleteFolderDialog({ open: false, folder: null })
      if (selectedFolderId === deleteFolderDialog.folder?.id) {
        handleSelectFolder(null)
      }
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const moveItemMutation = useMoveContentItem({
    onSuccess: () => {
      toast({ title: 'Item moved', description: 'The item has been moved successfully.' })
      setMoveItemOpen(false)
      setSelectedItem(null)
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const duplicateItemMutation = useDuplicateContentItem({
    onSuccess: () => {
      toast({ title: 'Item duplicated', description: 'The item has been duplicated successfully.' })
      setDuplicateItemOpen(false)
      setSelectedItem(null)
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const deleteItemMutation = useDeleteContentItem({
    onSuccess: () => {
      toast({ title: 'Item deleted', description: 'The item has been deleted successfully.' })
      setDeleteItemOpen(false)
      setSelectedItem(null)
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  // Auto-expand first level folders
  useEffect(() => {
    if (folderTree.length > 0 && expandedIds.size === 0) {
      setExpandedIds(new Set(folderTree.map((f) => f.id)))
    }
  }, [folderTree])

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_VIEW_KEY, viewMode)
  }, [viewMode])

  // Calculate total items across all folders
  const totalItemsInFolders = useMemo(() => {
    const countAll = (folders: ContentFolderTree[]): number =>
      folders.reduce((sum, folder) => sum + (folder.itemCount || 0) + countAll(folder.children || []), 0)
    return countAll(folderTree)
  }, [folderTree])

  // Filter folders by search
  const filteredFolders = useMemo(() => {
    if (!sidebarSearch.trim()) return folderTree
    const search = sidebarSearch.toLowerCase()
    const filterTree = (folders: ContentFolderTree[]): ContentFolderTree[] => {
      return folders
        .map((folder) => ({
          ...folder,
          children: folder.children ? filterTree(folder.children) : [],
        }))
        .filter(
          (folder) =>
            folder.name.toLowerCase().includes(search) ||
            folder.description?.toLowerCase().includes(search) ||
            (folder.children && folder.children.length > 0)
        )
    }
    return filterTree(folderTree)
  }, [folderTree, sidebarSearch])

  const items = itemsResponse?.value || []
  const totalCount = itemsResponse?.['@odata.count'] || items.length

  // Handlers
  const handleSelectFolder = (folderId: string | null) => {
    if (folderId) {
      searchParams.set('folder', folderId)
    } else {
      searchParams.delete('folder')
    }
    setSearchParams(searchParams)
  }

  const handleToggleExpand = (folderId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(folderId)) {
        next.delete(folderId)
      } else {
        next.add(folderId)
      }
      return next
    })
  }

  const handleOpenCreateFolder = (parentId?: string) => {
    setFolderDialog({ open: true, mode: 'create', parentId })
    setFolderName('')
    setFolderDescription('')
  }

  const handleOpenEditFolder = (folder: ContentFolderTree) => {
    setFolderDialog({ open: true, mode: 'edit', folder })
    setFolderName(folder.name)
    setFolderDescription(folder.description || '')
  }

  const handleFolderSubmit = async () => {
    if (!folderName.trim()) return
    if (folderDialog.mode === 'create') {
      await createFolderMutation.mutateAsync({
        name: folderName,
        description: folderDescription || undefined,
        parentFolderId: folderDialog.parentId,
      })
    } else if (folderDialog.folder) {
      await updateFolderMutation.mutateAsync({
        id: folderDialog.folder.id,
        data: { name: folderName, description: folderDescription || undefined },
      })
    }
  }

  const handleDeleteFolder = async () => {
    if (!deleteFolderDialog.folder) return
    await deleteFolderMutation.mutateAsync(deleteFolderDialog.folder.id)
  }

  const handleViewItem = useCallback((item: ContentItem) => {
    navigate(`/content-hub/item/${item.id}`)
  }, [navigate])

  const handleEditItem = useCallback((item: ContentItem) => {
    navigate(`/content-hub/item/${item.id}/edit`)
  }, [navigate])

  const handleMoveItem = useCallback((item: ContentItem) => {
    setSelectedItem(item)
    setMoveItemOpen(true)
  }, [])

  const handleDuplicateItem = useCallback((item: ContentItem) => {
    setSelectedItem(item)
    setDuplicateItemOpen(true)
  }, [])

  const handleDeleteItem = useCallback((item: ContentItem) => {
    setSelectedItem(item)
    setDeleteItemOpen(true)
  }, [])

  const handleSort = useCallback((field: string) => {
    setSortValue((prev) => {
      const [currentField, currentOrder] = prev.split('-')
      if (currentField === field) {
        return `${field}-${currentOrder === 'asc' ? 'desc' : 'asc'}`
      }
      return `${field}-asc`
    })
  }, [])

  return (
    <div className="flex h-[calc(100vh-4rem)] -m-6">
      {/* Sidebar - Folder Tree */}
      <div
        className={cn(
          'border-r border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50 flex flex-col transition-all duration-300',
          sidebarOpen ? 'w-72' : 'w-0'
        )}
      >
        {sidebarOpen && (
          <>
            {/* Sidebar Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Folder className="h-4 w-4 text-indigo-500" />
                  Folders
                </h2>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => handleOpenCreateFolder()}
                  title="Create Folder"
                >
                  <FolderPlus className="h-4 w-4" />
                </Button>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search folders..."
                  value={sidebarSearch}
                  onChange={(e) => setSidebarSearch(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
            </div>

            {/* Sidebar Content */}
            <ScrollArea className="flex-1">
              <div className="p-2">
                {/* All Content */}
                <button
                  onClick={() => handleSelectFolder(null)}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg transition-all mb-1',
                    'hover:bg-gray-100 dark:hover:bg-gray-800',
                    !selectedFolderId &&
                      'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 font-medium'
                  )}
                >
                  <div className="w-6 h-6 rounded bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                    <Database className="h-3.5 w-3.5 text-white" />
                  </div>
                  <span className="flex-1 text-left">All Content</span>
                  <span className="text-xs text-gray-400 tabular-nums">{totalItemsInFolders || totalCount}</span>
                </button>

                {/* Divider */}
                <div className="h-px bg-gray-200 dark:bg-gray-700 my-2" />

                {/* Folder Tree */}
                {foldersLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  </div>
                ) : filteredFolders.length === 0 ? (
                  <div className="text-center py-8">
                    <FolderOpen className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {sidebarSearch ? 'No folders found' : 'No folders yet'}
                    </p>
                    {!sidebarSearch && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleOpenCreateFolder()}
                        className="mt-1"
                      >
                        Create your first folder
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {filteredFolders.map((folder) => (
                      <FolderTreeItem
                        key={folder.id}
                        folder={folder}
                        level={0}
                        selectedFolderId={selectedFolderId}
                        expandedIds={expandedIds}
                        onSelect={handleSelectFolder}
                        onToggleExpand={handleToggleExpand}
                        onEdit={handleOpenEditFolder}
                        onDelete={(f) => setDeleteFolderDialog({ open: true, folder: f })}
                        onAddSubfolder={handleOpenCreateFolder}
                      />
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          </>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 pb-0">
          <div className="flex items-center gap-3 mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="h-8 w-8"
              title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
            >
              {sidebarOpen ? (
                <PanelLeftClose className="h-4 w-4" />
              ) : (
                <PanelLeftOpen className="h-4 w-4" />
              )}
            </Button>
            <div className="flex-1">
              {/* Custom Header with Folder Badge */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {selectedFolder && (
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold shadow-lg"
                      style={getColorStyle(selectedFolder.name, selectedFolder.color)}
                    >
                      {getFolderInitials(selectedFolder.name)}
                    </div>
                  )}
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedFolder ? selectedFolder.name : 'Content Hub'}
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {selectedFolder
                        ? selectedFolder.description || `${selectedFolder.itemCount || 0} items in this folder`
                        : 'Manage reusable content elements for your templates'}
                    </p>
                  </div>
                </div>
                <CreateNewDropdown
                  currentFolderId={selectedFolderId}
                  onCreateFolder={() => handleOpenCreateFolder(selectedFolderId || undefined)}
                />
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid gap-4 sm:grid-cols-4 mb-6">
            {[
              { label: 'Total Items', value: totalCount, color: 'from-indigo-400 to-indigo-600', icon: FileText },
              { label: 'Text Elements', value: items.filter((i) => i.type === 'Text').length, color: 'from-blue-400 to-blue-600', icon: FileText },
              { label: 'Data Points', value: items.filter((i) => i.type === 'DataPoint').length, color: 'from-green-400 to-green-600', icon: Hash },
              { label: 'Images', value: items.filter((i) => i.type === 'Image').length, color: 'from-purple-400 to-purple-600', icon: ImageIcon },
            ].map((stat) => (
              <FluentCard key={stat.label} className="flex items-center gap-4">
                <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color}`}>
                  <stat.icon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
                </div>
              </FluentCard>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden px-6 pb-6">
          <FluentCard className="h-full overflow-hidden flex flex-col">
            {/* Toolbar */}
            <div className="flex flex-col gap-3 p-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-1">
                  {/* Search */}
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search content..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  {/* Type Filter */}
                  <Select value={typeFilter} onValueChange={(value) => setTypeFilter(value as ContentTypeFilter)}>
                    <SelectTrigger
                      className={cn(
                        'w-[160px]',
                        typeFilter !== 'all' && 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30'
                      )}
                    >
                      <Filter className="h-4 w-4 mr-2 shrink-0" />
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTENT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="h-4 w-4 text-gray-500" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Sort Dropdown */}
                  <Select value={sortValue} onValueChange={setSortValue}>
                    <SelectTrigger className="w-[180px]">
                      <ArrowUpDown className="h-4 w-4 mr-2 shrink-0" />
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      {SORT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  {/* View Mode Toggle */}
                  <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={cn(
                        'p-2 rounded-md transition-colors',
                        viewMode === 'grid'
                          ? 'bg-white dark:bg-gray-700 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      )}
                      title="Grid view"
                    >
                      <Grid className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={cn(
                        'p-2 rounded-md transition-colors',
                        viewMode === 'list'
                          ? 'bg-white dark:bg-gray-700 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      )}
                      title="List view"
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Active Filters */}
              {(typeFilter !== 'all' || searchQuery.trim()) && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-gray-500">Active filters:</span>
                  {typeFilter !== 'all' && (
                    <Badge
                      variant="secondary"
                      className="gap-1 pl-2 pr-1 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300"
                    >
                      {CONTENT_TYPES.find((t) => t.value === typeFilter)?.label}
                      <button
                        onClick={() => setTypeFilter('all')}
                        className="ml-1 rounded-full p-0.5 hover:bg-indigo-200 dark:hover:bg-indigo-800"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}
                  {searchQuery.trim() && (
                    <Badge
                      variant="secondary"
                      className="gap-1 pl-2 pr-1 bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                    >
                      Search: "{searchQuery}"
                      <button
                        onClick={() => setSearchQuery('')}
                        className="ml-1 rounded-full p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}
                  <button
                    onClick={() => {
                      setTypeFilter('all')
                      setSearchQuery('')
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* Results count */}
            <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800/50">
              {isItemsLoading ? (
                <span>Loading...</span>
              ) : (
                <span>
                  Showing <span className="font-medium text-gray-700 dark:text-gray-300">{items.length}</span>
                  {totalCount !== items.length && (
                    <> of <span className="font-medium text-gray-700 dark:text-gray-300">{totalCount}</span></>
                  )}
                  {' '}item{totalCount !== 1 ? 's' : ''}
                  {(typeFilter !== 'all' || searchQuery.trim()) && ' matching your filters'}
                </span>
              )}
            </div>

            {/* Content List/Grid */}
            <div className="flex-1 overflow-auto p-4">
              {viewMode === 'grid' ? (
                <ContentItemGrid
                  items={items}
                  isLoading={isItemsLoading}
                  selectedItemIds={selectedItemIds}
                  onSelectionChange={setSelectedItemIds}
                  onItemClick={handleViewItem}
                  onItemDoubleClick={handleEditItem}
                  onItemView={handleViewItem}
                  onItemEdit={handleEditItem}
                  onItemDuplicate={handleDuplicateItem}
                  onItemMove={handleMoveItem}
                  onItemDelete={handleDeleteItem}
                  multiSelect
                  emptyMessage={
                    searchQuery || typeFilter !== 'all'
                      ? 'No items match your filters'
                      : selectedFolder
                      ? 'This folder is empty'
                      : 'No content items yet'
                  }
                  emptyAction={
                    !searchQuery && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          const queryParams = selectedFolderId ? `?folderId=${selectedFolderId}` : ''
                          navigate(`/content-hub/new/Text${queryParams}`)
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create your first item
                      </Button>
                    )
                  }
                />
              ) : (
                <ContentItemList
                  items={items}
                  isLoading={isItemsLoading}
                  selectedItemIds={selectedItemIds}
                  onSelectionChange={setSelectedItemIds}
                  onItemClick={handleViewItem}
                  onItemDoubleClick={handleEditItem}
                  onItemView={handleViewItem}
                  onItemEdit={handleEditItem}
                  onItemDuplicate={handleDuplicateItem}
                  onItemMove={handleMoveItem}
                  onItemDelete={handleDeleteItem}
                  multiSelect
                  sortBy={sortField}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  emptyMessage={
                    searchQuery || typeFilter !== 'all'
                      ? 'No items match your filters'
                      : selectedFolder
                      ? 'This folder is empty'
                      : 'No content items yet'
                  }
                  emptyAction={
                    !searchQuery && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          const queryParams = selectedFolderId ? `?folderId=${selectedFolderId}` : ''
                          navigate(`/content-hub/new/Text${queryParams}`)
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create your first item
                      </Button>
                    )
                  }
                />
              )}
            </div>
          </FluentCard>
        </div>
      </div>

      {/* Create/Edit Folder Dialog */}
      <Dialog open={folderDialog.open} onOpenChange={(open) => !open && setFolderDialog({ open: false, mode: 'create' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{folderDialog.mode === 'create' ? 'Create Folder' : 'Edit Folder'}</DialogTitle>
            <DialogDescription>
              {folderDialog.mode === 'create'
                ? 'Create a new folder to organize your content.'
                : 'Update the folder details.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="folder-name">Name</Label>
              <Input
                id="folder-name"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder="Enter folder name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="folder-description">Description (optional)</Label>
              <Input
                id="folder-description"
                value={folderDescription}
                onChange={(e) => setFolderDescription(e.target.value)}
                placeholder="Enter folder description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolderDialog({ open: false, mode: 'create' })}>
              Cancel
            </Button>
            <Button
              onClick={handleFolderSubmit}
              disabled={!folderName.trim() || createFolderMutation.isPending || updateFolderMutation.isPending}
            >
              {createFolderMutation.isPending || updateFolderMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Folder Dialog */}
      <AlertDialog
        open={deleteFolderDialog.open}
        onOpenChange={(open) => !open && setDeleteFolderDialog({ open: false, folder: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Folder</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteFolderDialog.folder?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteFolder}
              disabled={deleteFolderMutation.isPending}
            >
              {deleteFolderMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Item Dialogs */}
      <MoveItemDialog
        open={moveItemOpen}
        onOpenChange={setMoveItemOpen}
        item={selectedItem}
        folderTree={folderTree}
        onSubmit={async (folderId) => {
          if (!selectedItem) return
          await moveItemMutation.mutateAsync({
            id: selectedItem.id,
            data: { folderId: folderId || undefined },
          })
        }}
        isSubmitting={moveItemMutation.isPending}
      />

      <DuplicateItemDialog
        open={duplicateItemOpen}
        onOpenChange={setDuplicateItemOpen}
        item={selectedItem}
        folderTree={folderTree}
        onSubmit={async (data) => {
          if (!selectedItem) return
          await duplicateItemMutation.mutateAsync({ id: selectedItem.id, data })
        }}
        isSubmitting={duplicateItemMutation.isPending}
      />

      <DeleteItemDialog
        open={deleteItemOpen}
        onOpenChange={setDeleteItemOpen}
        item={selectedItem}
        onConfirm={async () => {
          if (!selectedItem) return
          await deleteItemMutation.mutateAsync(selectedItem.id)
        }}
        isSubmitting={deleteItemMutation.isPending}
      />
    </div>
  )
}
